import React from 'react';
import { Loader, Radio, RadioGroup, Stack } from 'rsuite';
import { useCartQuery } from '@/api/useCartQuery';
import { useUpdateCart } from '@/api/useUpdateCart';
import type { ShippingMethodOption } from '@/types';
import { cn } from '@bem-react/classname';
import { useCurrenciesStore } from '@/state/currenciesStore';
import { convertCurrencyAmount, formatCurrency } from '@/utils/currencyUtils';
import styles from './ShippingMethods.module.scss';

export type ShippingMethodsProps = {
  // Controlled selected handle
  value?: string | null;
  // Disable interaction
  disabled?: boolean;
  // Optional heading
  title?: string;
  // Optional className
  className?: string;
};

export default function ShippingMethods({
  value,
  disabled,
  title = 'Shipping method',
  className,
}: ShippingMethodsProps) {
  const cName = cn(styles.cShippingMethods);
  const { data: cart, isLoading } = useCartQuery();
  const updateCart = useUpdateCart();

  const { primaryIso, rates } = useCurrenciesStore();
  const paymentIso = cart?.paymentCurrency;

  const options = React.useMemo(() => {
    const map = (cart?.availableShippingMethodOptions ?? {}) as Record<string, ShippingMethodOption>;
    return Object.entries(map).map(([handle, opt]) => {
      const rawPrice = typeof opt?.price === 'number' ? opt.price as number : undefined;
      let displayPrice: string | undefined = undefined;

      if (rawPrice !== undefined && primaryIso && paymentIso) {
        const converted = convertCurrencyAmount(rawPrice, rates, primaryIso, paymentIso);
        displayPrice = formatCurrency(converted, paymentIso);
      } else if (opt?.priceAsCurrency) {
        displayPrice = opt.priceAsCurrency;
      }

      return {
        handle,
        name: opt?.name ?? handle,
        priceAsCurrency: displayPrice,
        price: rawPrice,
        enabled: opt?.enabled !== false,
      };
    });
  }, [cart?.availableShippingMethodOptions, primaryIso, paymentIso, rates]);

  const selectedValue = value ?? cart?.shippingMethodHandle ?? undefined;

  if (isLoading) {
    return (
      <div style={{ padding: 12 }}>
        <Loader />
      </div>
    );
  }

  if (!options.length) {
    return <></>;
  }

  return (
    <div className={`${cName()}${className ? ` ${className}` : ''}`}>
      {title ? (
        <h3 className={cName('Title')}>{title}</h3>
      ) : null}
      <RadioGroup
        name="shippingMethod"
        value={selectedValue as any}
        onChange={(val) => updateCart.mutate({ shippingMethodHandle: String(val) })}
        disabled={disabled || updateCart.isPending}
      >
        {options.map((opt) => (
          <Radio key={opt.handle} value={opt.handle} disabled={!opt.enabled}>
            <Stack spacing={8} alignItems="center">
              <span>{opt.name}</span>
              {opt.priceAsCurrency ? (
                <span style={{ opacity: 0.7 }}>— {opt.priceAsCurrency}</span>
              ) : null}
            </Stack>
          </Radio>
        ))}
      </RadioGroup>
    </div>
  );
}
